<template>
  <div class="store-management">
    <div class="page-header">
      <h2>门店管理</h2>
      <p>管理所有门店信息</p>
    </div>
    
    <div class="page-content">
      <!-- 搜索区域 -->
      <div class="search-section">
        <div class="search-box">
          <input
            v-model="searchKeyword"
            type="text"
            placeholder="请输入门店名称或编码搜索"
            class="search-input"
            @keyup.enter="handleSearch"
          />
          <button @click="handleSearch" class="search-btn">搜索</button>
          <button @click="handleReset" class="reset-btn">重置</button>
        </div>
      </div>

      <!-- 表格区域 -->
      <div class="table-section">
        <div class="table-header">
          <h3>门店列表</h3>
          <button @click="handleAdd" class="add-btn">新增门店</button>
        </div>
        
        <div class="table-container">
          <table class="data-table">
            <thead>
              <tr>
                <th>门店名称</th>
                <th>地址</th>
                <th>描述</th>
                <th>门店管理员</th>
                <th>状态</th>
                <th>客户端公告</th>
                <th>会员充值优惠公告</th>
                <th>客服热线</th>
                <th>创建时间</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="loading" class="loading-row">
                <td colspan="10" class="loading-cell">
                  <div class="loading-spinner">
                    <div class="spinner"></div>
                  </div>
                </td>
              </tr>
              <tr v-else-if="!storeList || storeList.length === 0" class="empty-row">
                <td colspan="10" class="empty-cell">暂无数据</td>
              </tr>
              <tr v-else v-for="store in storeList" :key="store.id" class="data-row">
                <td>{{ store.storeName }}</td>
                <td>{{ store.address }}</td>
                <td>{{ store.description }}</td>
                <td>{{ getStoreAdmins(store.storeAdminVOS) }}</td>
                <td>
                  <span :class="['status-badge', store.status ? 'active' : 'inactive']">
                    {{ store.status ? '启用' : '禁用' }}
                  </span>
                </td>
                <td>{{ store.gongGao || '-' }}</td>
                <td>{{ store.rechargeDiscountInformation || '-' }}</td>
                <td>{{ formatCustomerService(store) }}</td>
                <td>{{ formatDate(store.createdTime) }}</td>
                <td>
                  <div class="action-group">
                    <button @click="handleEdit(store)" class="action-btn edit-btn">编辑</button>
                    <button @click="handleDelete(store)" class="action-btn delete-btn">删除</button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- 分页区域 -->
        <div class="pagination-section" v-if="!loading && storeList && storeList.length > 0">
          <div class="pagination-info">
            共 {{ pagination.totalElements }} 条记录，第 {{ pagination.number + 1 }} / {{ pagination.totalPages }} 页
          </div>
          <div class="pagination-controls">
            <button 
              @click="handlePageChange(0)" 
              :disabled="pagination.first"
              class="page-btn"
            >
              首页
            </button>
            <button 
              @click="handlePageChange(pagination.number - 1)" 
              :disabled="pagination.first"
              class="page-btn"
            >
              上一页
            </button>
            <span class="page-info">
              {{ pagination.number + 1 }} / {{ pagination.totalPages }}
            </span>
            <button 
              @click="handlePageChange(pagination.number + 1)" 
              :disabled="pagination.last"
              class="page-btn"
            >
              下一页
            </button>
            <button 
              @click="handlePageChange(pagination.totalPages - 1)" 
              :disabled="pagination.last"
              class="page-btn"
            >
              末页
            </button>
          </div>
        </div>
      </div>
    </div>
    
    <!-- 新增门店弹窗 -->
    <AddStoreModal 
      :visible="showAddModal" 
      @close="handleCloseAddModal"
      @success="handleAddSuccess"
    />
    
    <!-- 编辑门店弹窗 -->
    <EditStoreModal 
      :visible="showEditModal" 
      :store-data="currentStore"
      @close="handleCloseEditModal"
      @success="handleEditSuccess"
    />
  </div>
</template>

<script>
import request from '../utils/request'
import AddStoreModal from './AddStoreModal.vue'
import EditStoreModal from './EditStoreModal.vue'

export default {
  name: 'StoreManagement',
  components: {
    AddStoreModal,
    EditStoreModal
  },
  data() {
    return {
      storeList: [],
      loading: false,
      searchKeyword: '',
      showAddModal: false,
      showEditModal: false,
      currentStore: null,
      pagination: {
        number: 0,
        size: 20,
        totalElements: 0,
        totalPages: 0,
        first: true,
        last: true
      }
    }
  },
  mounted() {
    this.loadStoreList()
  },
  methods: {
    async loadStoreList() {
      this.loading = true
      try {
        const response = await request.get('/api/stores/getStores', {
          params: {
            page: this.pagination.number,
            size: this.pagination.size,
            keyword: this.searchKeyword || undefined
          }
        })

        if (response.data.success && response.data.code === 200) {
          this.storeList = response.data.data.content || []
          this.pagination = {
            number: response.data.data.number || 0,
            size: response.data.data.size || 20,
            totalElements: response.data.data.totalElements || 0,
            totalPages: response.data.data.totalPages || 0,
            first: response.data.data.first || true,
            last: response.data.data.last || true
          }
        } else {
          throw new Error(response.data.message || '获取门店列表失败')
        }
      } catch (error) {
        console.error('获取门店列表失败:', error)
        let errorMessage = '获取门店列表失败'
        
        if (error.response) {
          errorMessage = error.response.data.message || `服务器错误: ${error.response.status}`
        } else if (error.request) {
          errorMessage = '网络连接失败，请检查网络或服务器状态'
        } else {
          errorMessage = error.message || '获取门店列表失败，请重试'
        }
        
        alert(errorMessage)
        this.storeList = []
        // 重置分页信息
        this.pagination = {
          number: 0,
          size: 20,
          totalElements: 0,
          totalPages: 0,
          first: true,
          last: true
        }
      } finally {
        this.loading = false
      }
    },

    handleSearch() {
      this.pagination.number = 0 // 重置到第一页
      this.loadStoreList()
    },

    handleReset() {
      this.searchKeyword = ''
      this.pagination.number = 0
      this.loadStoreList()
    },

    handlePageChange(pageNumber) {
      if (pageNumber >= 0 && pageNumber < this.pagination.totalPages) {
        this.pagination.number = pageNumber
        this.loadStoreList()
      }
    },

    handleAdd() {
      this.showAddModal = true
    },
    
    handleCloseAddModal() {
      this.showAddModal = false
    },
    
    handleAddSuccess() {
      // 新增成功后刷新列表
      this.loadStoreList()
    },
    
    handleCloseEditModal() {
      this.showEditModal = false
      this.currentStore = null
    },
    
    handleEditSuccess() {
      // 编辑成功后刷新列表
      this.loadStoreList()
    },

    handleEdit(store) {
      this.currentStore = store
      this.showEditModal = true
    },

    async handleDelete(store) {
      if (confirm(`确定要删除门店 "${store.storeName}" 吗？\n\n此操作不可撤销！`)) {
        await this.deleteStore(store.id)
      }
    },
    
    async deleteStore(storeId) {
      try {
        const response = await request.delete(`/api/stores/deleteStore?id=${storeId}`)
        
        if (response.data.success && response.data.code === 200) {
          // 删除成功后刷新列表
          this.loadStoreList()
        } else {
          throw new Error(response.data.message || '删除门店失败')
        }
        
      } catch (error) {
        console.error('删除门店失败:', error)
        let errorMessage = '删除门店失败'
        
        if (error.response) {
          errorMessage = error.response.data.message || `服务器错误: ${error.response.status}`
        } else if (error.request) {
          errorMessage = '网络连接失败，请检查网络或服务器状态'
        } else {
          errorMessage = error.message || '删除门店失败，请重试'
        }
        
        alert(errorMessage)
      }
    },

    formatDate(dateString) {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      })
    },

    getStoreAdmins(storeAdminVOS) {
      if (!storeAdminVOS || !Array.isArray(storeAdminVOS) || storeAdminVOS.length === 0) {
        return '-'
      }
      return storeAdminVOS.map(admin => admin.adminName).join(', ')
    },

    formatCustomerService(store) {
      const phone = store.keFuPhone || ''
      if (!phone) {
        return '-'
      }
      const label = store.customerServiceLabel || '客服热线'
      return `${label}: ${phone}`
    }
  }
}
</script>

<style scoped>
.store-management {
  height: 100%;
}

.page-header {
  margin-bottom: 24px;
}

.page-header h2 {
  font-size: 24px;
  color: #333;
  margin: 0 0 8px 0;
}

.page-header p {
  color: #666;
  margin: 0;
  font-size: 14px;
}

.page-content {
  background: white;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* 搜索区域 */
.search-section {
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #f0f0f0;
}

.search-box {
  display: flex;
  gap: 12px;
  align-items: center;
}

.search-input {
  flex: 1;
  max-width: 300px;
  padding: 8px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.3s ease;
}

.search-input:focus {
  outline: none;
  border-color: #1890ff;
  box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
}

.search-btn, .reset-btn {
  padding: 8px 16px;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  background: white;
  color: #333;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s ease;
}

.search-btn {
  background: #1890ff;
  color: white;
  border-color: #1890ff;
}

.search-btn:hover {
  background: #40a9ff;
  border-color: #40a9ff;
}

.reset-btn:hover {
  background: #f5f5f5;
  border-color: #40a9ff;
}

/* 表格区域 */
.table-section {
  margin-bottom: 24px;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.table-header h3 {
  font-size: 18px;
  color: #333;
  margin: 0;
}

.add-btn {
  padding: 8px 16px;
  background: #52c41a;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.3s ease;
}

.add-btn:hover {
  background: #73d13d;
}

.table-container {
  overflow-x: auto;
  border: 1px solid #f0f0f0;
  border-radius: 6px;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.data-table th {
  background: #fafafa;
  padding: 12px 16px;
  text-align: left;
  font-weight: 600;
  color: #333;
  border-bottom: 1px solid #f0f0f0;
}

.data-table th:nth-child(5),
.data-table td:nth-child(5) {
  width: 100px;
  min-width: 100px;
}

.data-table th:nth-child(6),
.data-table td:nth-child(6),
.data-table th:nth-child(7),
.data-table td:nth-child(7) {
  max-width: 200px;
  word-wrap: break-word;
  word-break: break-all;
}

.data-table th:nth-child(8),
.data-table td:nth-child(8) {
  width: 120px;
  min-width: 120px;
}

.data-table td {
  padding: 12px 16px;
  border-bottom: 1px solid #f0f0f0;
  color: #666;
}

.data-row:hover {
  background: #f5f5f5;
}

.loading-cell, .empty-cell {
  text-align: center;
  color: #999;
  font-style: italic;
}

.loading-spinner {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px 0;
}

.spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #1890ff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.status-badge {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.status-badge.active {
  background: #f6ffed;
  color: #52c41a;
  border: 1px solid #b7eb8f;
}

.status-badge.inactive {
  background: #fff2e8;
  color: #fa8c16;
  border: 1px solid #ffd591;
}

.action-btn {
  padding: 4px 8px;
  margin-right: 8px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  background: white;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.3s ease;
}

.action-group {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
}

.action-group .action-btn {
  margin-right: 0;
}

.edit-btn {
  color: #1890ff;
  border-color: #1890ff;
}

.edit-btn:hover {
  background: #e6f7ff;
  border-color: #40a9ff;
}

.delete-btn {
  color: #ff4d4f;
  border-color: #ff4d4f;
}

.delete-btn:hover {
  background: #fff2f0;
  border-color: #ff7875;
}

/* 分页区域 */
.pagination-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 0;
  border-top: 1px solid #f0f0f0;
}

.pagination-info {
  color: #666;
  font-size: 14px;
}

.pagination-controls {
  display: flex;
  gap: 8px;
  align-items: center;
}

.page-btn {
  padding: 6px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  background: white;
  color: #333;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s ease;
}

.page-btn:hover:not(:disabled) {
  background: #f5f5f5;
  border-color: #40a9ff;
}

.page-btn:disabled {
  background: #f5f5f5;
  color: #bfbfbf;
  cursor: not-allowed;
  border-color: #d9d9d9;
}

.page-info {
  padding: 6px 12px;
  color: #666;
  font-size: 14px;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .search-box {
    flex-direction: column;
    align-items: stretch;
  }
  
  .search-input {
    max-width: none;
  }
  
  .table-header {
    flex-direction: column;
    gap: 12px;
    align-items: stretch;
  }
  
  .pagination-section {
    flex-direction: column;
    gap: 12px;
    align-items: center;
  }
  
  .pagination-controls {
    flex-wrap: wrap;
    justify-content: center;
  }
}
</style>
